// @owners { team: patients }
import { Button, Column, XsSpacing } from '@alto/design-system';
import { useNavigation } from '@alto/navigation';
import { type Prescription } from '@alto/scriptdash/alto/patient_app/homescreen/types/v1/prescription';
import { type SourcePrescription } from '@alto/scriptdash/alto/patient_app/homescreen/types/v1/source_prescription';
import React, { useEffect } from 'react';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { type Prescription as OGPrescription } from '~shared/types';
import { WaitingOnProgynyTag } from './WaitingOnProgynyTag';

type Props = {
  readonly prescription: SourcePrescription | Prescription | OGPrescription;
  readonly onRxStatusTagPress?: () => void;
  readonly inlineButton?: boolean;
  /** Whether to render the "waiting on progyny" tag above the button. Defaults to true. */
  readonly showTag?: boolean;
  /** Whether the button uses the smaller size. Defaults to true. */
  readonly small?: boolean;
};

const buttonLabel = 'Order early';

export const OrderEarlyCTA = ({
  inlineButton,
  prescription,
  onRxStatusTagPress,
  showTag = true,
  small = true,
}: Props) => {
  const { navigate } = useNavigation();
  const { trackEvent } = useAnalytics();
  const prescriptionId = 'id' in prescription ? prescription.id : null;

  useEffect(() => {
    trackEvent({
      event: EVENTS.PROGYNY_FERTILITY_CYCLE_FORM_PROMPT_VIEWED,
      params: { prescriptionId },
    });
  }, [trackEvent, prescriptionId]);

  if (!prescriptionId) {
    return null;
  }

  const handlePress = () => {
    trackEvent({
      event: EVENTS.PROGYNY_FERTILITY_CYCLE_FORM_PROMPT_PRESSED,
      params: { prescriptionId, prompt: buttonLabel },
    });
    navigate('RouteOrderEarlyForm', { prescriptionId });
  };
  return (
    <Column>
      {showTag ? (
        <>
          <WaitingOnProgynyTag onPress={onRxStatusTagPress} />
          <XsSpacing />
        </>
      ) : null}
      <Button
        onPress={handlePress}
        label={buttonLabel}
        small={small}
        type="secondary"
        width={inlineButton ? 'inline' : undefined}
      />
    </Column>
  );
};
